import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './ui/Avatar';

interface NavItem {
  id: string;
  to: string;
  label: string;
  icon: React.ReactNode;
  roles: string[];
  count?: number;
  urgent?: boolean;
}

const iconPath = (d: string) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS = {
  home:      'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1V10.5Z',
  building:  'M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M5 21h14M9 7h2M9 11h2M9 15h2M15 11h4v10',
  doc:       'M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM14 3v4h4M9 13h6M9 17h6',
  coin:      'M12 3v18M16 7c-1-1.2-2.5-2-4-2-2.5 0-4 1.3-4 3s2 2.5 4 3 4 1.3 4 3-1.5 3-4 3c-1.5 0-3-.8-4-2',
  wrench:    'M14.7 6.3a4 4 0 0 1 5.4 5.4l-9.5 9.5a2.1 2.1 0 0 1-3-3l9.5-9.5-2.4-2.4Z',
  chat:      'M21 12c0 4.4-4 8-9 8a10 10 0 0 1-3-.5L4 21l1.5-4A8 8 0 0 1 3 12c0-4.4 4-8 9-8s9 3.6 9 8Z',
  chart:     'M4 20V8M10 20V4M16 20v-8M22 20H2',
  settings:  'M12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM19.4 14a1 1 0 0 0 .2 1.1l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1 1 0 0 0-1.1-.2 1 1 0 0 0-.6.9V19a2 2 0 1 1-4 0v-.1a1 1 0 0 0-.6-.9 1 1 0 0 0-1.1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1 1 0 0 0 .2-1.1 1 1 0 0 0-.9-.6H5a2 2 0 1 1 0-4h.1a1 1 0 0 0 .9-.6 1 1 0 0 0-.2-1.1l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1 1 0 0 0 1.1.2H10a1 1 0 0 0 .6-.9V5a2 2 0 1 1 4 0v.1a1 1 0 0 0 .6.9 1 1 0 0 0 1.1-.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1 1 0 0 0-.2 1.1V10a1 1 0 0 0 .9.6H19a2 2 0 1 1 0 4h-.1a1 1 0 0 0-.9.6Z',
  clipboard: 'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 7h6m-6 4h6',
  users:     'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM2 21a7 7 0 0 1 14 0M17 11a4 4 0 0 0 0-8M22 21a7 7 0 0 0-5-6.7',
  logout:    'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  alert:     'M12 9v4M12 17h.01M10.3 3.9 2.5 17a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  shield:    'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
};

const navItems: NavItem[] = [
  // Renter
  { id: 'home',   to: '/renter/dashboard',      label: 'Dashboard',    icon: iconPath(ICONS.home),   roles: ['RENTER'] },
  { id: 'pay',    to: '/renter/pay-rent',        label: 'Pay Rent',     icon: iconPath(ICONS.coin),   roles: ['RENTER'] },
  { id: 'maint',  to: '/maintenance/track',      label: 'Maintenance',  icon: iconPath(ICONS.wrench), roles: ['RENTER'] },
  { id: 'docs',   to: '/documents',              label: 'Documents',    icon: iconPath(ICONS.doc),    roles: ['RENTER'] },
  { id: 'msgs',   to: '/messages',               label: 'Messages',     icon: iconPath(ICONS.chat),   roles: ['RENTER'] },
  { id: 'compl',  to: '/complaints/new',         label: 'File Complaint', icon: iconPath(ICONS.alert), roles: ['RENTER'] },
  // Owner
  { id: 'home',   to: '/owner/dashboard',        label: 'Dashboard',    icon: iconPath(ICONS.home),      roles: ['OWNER'] },
  { id: 'props',  to: '/owner/properties',       label: 'Properties',   icon: iconPath(ICONS.building),  roles: ['OWNER'] },
  { id: 'leases', to: '/owner/leases',           label: 'Leases',       icon: iconPath(ICONS.doc),       roles: ['OWNER'] },
  { id: 'ledger', to: '/owner/ledger',           label: 'Rent Ledger',  icon: iconPath(ICONS.coin),      roles: ['OWNER'] },
  { id: 'tax',    to: '/owner/tax-reports',      label: 'Tax Reports',  icon: iconPath(ICONS.chart),     roles: ['OWNER'] },
  { id: 'comp',   to: '/owner/compliance',       label: 'Compliance',   icon: iconPath(ICONS.clipboard), roles: ['OWNER'] },
  { id: 'msgs',   to: '/messages',              label: 'Messages',     icon: iconPath(ICONS.chat),      roles: ['OWNER'] },
  // Agent
  { id: 'home',   to: '/agent/dashboard',        label: 'Dashboard',    icon: iconPath(ICONS.home),      roles: ['AGENT'] },
  { id: 'props',  to: '/agent/properties',       label: 'Properties',   icon: iconPath(ICONS.building),  roles: ['AGENT'] },
  { id: 'leases', to: '/agent/leases',           label: 'Leases',       icon: iconPath(ICONS.doc),       roles: ['AGENT'] },
  { id: 'maint',  to: '/agent/maintenance',      label: 'Maintenance',  icon: iconPath(ICONS.wrench),    roles: ['AGENT'] },
  { id: 'insp',   to: '/agent/inspections',      label: 'Inspections',  icon: iconPath(ICONS.clipboard), roles: ['AGENT'] },
  { id: 'msgs',   to: '/messages',              label: 'Messages',     icon: iconPath(ICONS.chat),      roles: ['AGENT'] },
  // Contractor
  { id: 'home',   to: '/contractor/dashboard',   label: 'Dashboard',    icon: iconPath(ICONS.home),   roles: ['CONTRACTOR'] },
  { id: 'jobs',   to: '/contractor/jobs',        label: 'My Jobs',      icon: iconPath(ICONS.wrench), roles: ['CONTRACTOR'] },
  // Admin
  { id: 'home',   to: '/admin/dashboard',        label: 'Dashboard',    icon: iconPath(ICONS.home),     roles: ['ADMIN'] },
  { id: 'agency', to: '/admin/agencies',          label: 'Agencies',     icon: iconPath(ICONS.building), roles: ['ADMIN'] },
  { id: 'users',  to: '/admin/users',            label: 'Users',        icon: iconPath(ICONS.users),    roles: ['ADMIN'] },
  { id: 'props',  to: '/admin/properties',       label: 'Properties',   icon: iconPath(ICONS.building), roles: ['ADMIN'] },
  { id: 'audit',  to: '/admin/audit',            label: 'Audit Log',    icon: iconPath(ICONS.shield),   roles: ['ADMIN'] },
  { id: 'stgs',   to: '/admin/settings',         label: 'Settings',     icon: iconPath(ICONS.settings), roles: ['ADMIN'] },
  // Agency Admin (ADMIN role + agencyId)
  { id: 'home',   to: '/agency/dashboard',        label: 'Dashboard',    icon: iconPath(ICONS.home),     roles: ['AGENCY_ADMIN'] },
  { id: 'stgs',   to: '/admin/settings',          label: 'Settings',     icon: iconPath(ICONS.settings), roles: ['AGENCY_ADMIN'] },
];

const roleLabel: Record<string, string> = {
  RENTER: 'Renter', OWNER: 'Owner', AGENT: 'Agent', CONTRACTOR: 'Contractor',
  ADMIN: 'Admin', AGENCY_ADMIN: 'Agency Admin',
};

const Rail: React.FC = () => {
  const { user, role, logout } = useAuth();
  const location = useLocation();

  // Distinguish platform admin from agency admin
  const effectiveRole = role === 'ADMIN' && user?.agencyId ? 'AGENCY_ADMIN' : role;
  const links = navItems.filter(l => effectiveRole && l.roles.includes(effectiveRole));

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <div
      className="hidden md:flex flex-col flex-none"
      style={{ width: 'var(--rail-w)', background: 'var(--forest-900)', height: '100vh', position: 'sticky', top: 0, overflow: 'hidden' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm flex-none"
          style={{ background: 'var(--teal-400)', color: 'var(--forest-900)', letterSpacing: '-0.04em', fontSize: 16 }}
        >
          R
        </div>
        <div className="min-w-0">
          <div className="text-white font-semibold text-sm leading-none">Rentry</div>
          <div className="text-[11px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {role ? roleLabel[effectiveRole ?? role] : ''}
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <div className="text-[10px] font-semibold tracking-widest uppercase px-2 pb-2 pt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Workspace
        </div>
        <ul className="space-y-0.5">
          {links.map((item, i) => {
            const active = isActive(item.to);
            return (
              <li key={i}>
                <Link
                  to={item.to}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-[13.5px] no-underline"
                  style={{
                    color: active ? '#fff' : 'rgba(255,255,255,0.72)',
                    background: active ? 'rgba(255,255,255,0.10)' : 'transparent',
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  <span style={{ color: active ? '#fff' : 'rgba(255,255,255,0.65)', display: 'flex', flexShrink: 0 }}>
                    {item.icon}
                  </span>
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.count && (
                    <span
                      className="text-[11px] font-semibold rounded-full px-1.5 py-px"
                      style={{
                        background: item.urgent ? 'var(--coral-400)' : 'rgba(255,255,255,0.15)',
                        color: item.urgent ? '#fff' : 'rgba(255,255,255,0.9)',
                      }}
                    >
                      {item.count}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User footer */}
      <div className="px-3 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-2.5">
          <Avatar name={user?.name || user?.email || '?'} size={30} />
          <div className="flex-1 min-w-0">
            <div className="text-white text-[13px] font-medium truncate leading-none">
              {user?.firstName || user?.name || user?.email}
            </div>
            <div className="text-[11px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {user?.email}
            </div>
          </div>
          <button
            onClick={logout}
            className="flex-none w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
            style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)' }}
            title="Sign out"
          >
            {iconPath(ICONS.logout)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Rail;
